import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { formatGermanTime } from '../../helpers/germanTime';
import Editor from '@monaco-editor/react';
import { api, fetchData } from '../../helpers/apiUtils';
import {
  LIVESTREAM_LANDING_URL,
  Redlink,
  REDLINK_DATE_TOKEN_RE,
  REDLINK_LINK_TOKEN_RE,
  RedlinkAuthor,
  redlinkLinkToken,
  RedlinkLivestream,
  RedlinkProduct,
  RedlinkStatus,
  RedlinkTargetType,
  RedlinkWebinar,
  RedlinkWkz,
} from '../../helpers/redlinkData';
import TinyMCEEditor, { TinyMCEEditorRef } from '../../components/TinyMCEEditor';
import SpamScorePanel from '../../components/SpamScorePanel';
import TestEmailDialog, { TestEmailClient } from '../../components/TestEmailDialog';
import { useUrlValidation } from '../../hooks/useUrlValidation';

interface RedlinkEditorProps {
  redlinkId: number | null;
  onSaved: () => void;
  onCancel: () => void;
}

type EditorMode = 'wysiwyg' | 'code';

// The newsletter send is a single SMTP path — TestEmailDialog is client-centric,
// so we hand it one synthetic client to satisfy its "pick a client" contract.
const REDLINK_TEST_CLIENT: TestEmailClient = {
  code: 'ft',
  name: 'Finanztrends Newsletter',
  esp_type: 'iterable',
};

const STATUS_LABELS: Record<RedlinkStatus, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  paused: 'Pausiert',
  archived: 'Archiviert',
};

const STATUS_COLORS: Record<RedlinkStatus, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  archived: 'default',
};

const RedlinkEditor: React.FC<RedlinkEditorProps> = ({ redlinkId, onCancel }) => {
  const [, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Snackbar (used for the 409 schedule conflict + send-test warnings)
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Form state
  const [targetType, setTargetType] = useState<RedlinkTargetType>('plx');
  const [productId, setProductId] = useState<number | ''>('');
  const [wkzCode, setWkzCode] = useState<string>('');
  const [webinarId, setWebinarId] = useState<string>('');
  const [headline, setHeadline] = useState('');
  const [concept, setConcept] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorImageUrl, setAuthorImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [status, setStatus] = useState<RedlinkStatus>('draft');
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  // Geplante Live-Sendungen für den Zieltyp 'livestream'. Rein informativ: der
  // Redlink wird über sein Pin-Datum einer Sendung zugeordnet, und ein Pin auf
  // einen Tag ohne Sendung fällt beim Versand still auf den Evergreen zurück.
  // Ohne diese Anzeige wäre das für den Redakteur unsichtbar.
  const [livestreams, setLivestreams] = useState<RedlinkLivestream[]>([]);

  // Publish controls
  const [pinDate, setPinDate] = useState('');

  // Reference data
  const [products, setProducts] = useState<RedlinkProduct[]>([]);
  const [wkzConfigs, setWkzConfigs] = useState<RedlinkWkz[]>([]);
  const [webinars, setWebinars] = useState<RedlinkWebinar[]>([]);
  const [authors, setAuthors] = useState<RedlinkAuthor[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Editor mode (persisted) + refs-only editor handles
  const [editorMode, setEditorMode] = useState<EditorMode>(() => {
    const saved = localStorage.getItem('redlink-editor-mode');
    return saved === 'code' || saved === 'wysiwyg' ? saved : 'wysiwyg';
  });
  const editorRef = useRef<TinyMCEEditorRef | null>(null);
  const monacoRef = useRef<any>(null);

  // URL reachability validation
  const { validateUrl, getState, resetField, hasUnreachableUrls, isValidating } =
    useUrlValidation();
  const landingUrlState = getState('landing_url');

  // Test email dialog
  const [testEmailOpen, setTestEmailOpen] = useState(false);

  // Load reference data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [productRes, webinarRes, authorRes, liveRes] = await Promise.all([
          fetchData<RedlinkProduct[]>('/api/newsletter-redlinks/products'),
          fetchData<RedlinkWebinar[]>('/api/newsletter-redlinks/webinars'),
          fetchData<RedlinkAuthor[]>('/api/newsletter-redlinks/authors'),
          fetchData<RedlinkLivestream[]>('/api/newsletter-redlinks/livestreams'),
        ]);
        if (cancelled) return;
        if (productRes.success && productRes.data) setProducts(productRes.data);
        if (webinarRes.success && webinarRes.data) setWebinars(webinarRes.data);
        if (authorRes.success && authorRes.data) setAuthors(authorRes.data);
        if (liveRes.success && liveRes.data) setLivestreams(liveRes.data);
      } catch (err) {
        console.error('Error loading redlink reference data:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load WKZ configs when product changes
  useEffect(() => {
    if (!productId) {
      setWkzConfigs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchData<RedlinkWkz[]>(
          `/api/newsletter-redlinks/products/${productId}/wkz`,
        );
        if (!cancelled && res.success && res.data) setWkzConfigs(res.data);
      } catch (err) {
        console.error('Error loading WKZ configs:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Load existing redlink
  useEffect(() => {
    if (!redlinkId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetchData<Redlink>(`/api/newsletter-redlinks/${redlinkId}`);
        if (cancelled) return;
        if (res.success && res.data) {
          const r = res.data;
          setTargetType(r.target_type);
          setProductId(r.product_id ?? '');
          setWebinarId(r.webinar_id ?? '');
          setWkzCode(r.wkz_code ?? '');
          setHeadline(r.headline);
          setConcept(r.concept ?? '');
          setLandingUrl(r.landing_url);
          setBodyHtml(r.body_html);
          setAuthorName(r.author_name ?? '');
          setAuthorImageUrl(r.author_image_url ?? '');
          setStatus(r.status);
          setScheduledDate(r.scheduled_date);
          setPinDate(r.scheduled_date ?? '');
        } else {
          setError(res.error || 'Redlink konnte nicht geladen werden');
        }
      } catch (err) {
        setError('Fehler beim Laden des Redlinks');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [redlinkId]);

  // Read current HTML from whichever editor is active (refs-only — never via
  // per-keystroke state).
  const getCurrentHtml = useCallback((): string => {
    if (editorMode === 'code' && monacoRef.current) {
      return monacoRef.current.getValue() || '';
    }
    if (editorRef.current) {
      return editorRef.current.getContent();
    }
    return bodyHtml;
  }, [editorMode, bodyHtml]);

  const handleEditorModeChange = (_: React.MouseEvent<HTMLElement>, newMode: EditorMode | null) => {
    if (!newMode || newMode === editorMode) return;
    // Persist current content before swapping editors so the target picks it up.
    setBodyHtml(getCurrentHtml());
    setEditorMode(newMode);
    localStorage.setItem('redlink-editor-mode', newMode);
  };

  const handleTargetTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    next: RedlinkTargetType | null,
  ) => {
    if (!next || next === targetType) return;
    setTargetType(next);
    // Der Prüfzustand gehört zur ALTEN URL. Ohne dieses Zurücksetzen trägt
    // `hasUnreachableUrls()` ihn weiter und deaktiviert den Speichern-Knopf
    // (Zeile ~1024) — bei 'livestream' sogar unsichtbar, weil das Feld dort
    // die feste URL ohne Fehleranzeige rendert. Jeder Wechsel wechselt die
    // relevante URL, deshalb unbedingt und nicht je Zweig.
    resetField('landing_url');
    // Die jetzt gegenstandslose Zielreferenz leeren. Dreiwertig, nicht per else:
    // 'livestream' DARF ein Produkt behalten (optional, nur Beschriftung), und
    // seine Ziel-URL ist fest — der alte Webinar-Signup-Link darf nicht
    // stehenbleiben, sonst zeigt das gesperrte Feld etwas anderes an, als
    // gespeichert wird.
    if (next === 'plx') {
      setWebinarId('');
      setLandingUrl('');
    } else if (next === 'webinar') {
      setProductId('');
      setWkzCode('');
      setLandingUrl('');
    } else {
      setWebinarId('');
      setWkzCode('');
      setLandingUrl(LIVESTREAM_LANDING_URL);
    }
  };

  const handleWebinarChange = (value: string) => {
    setWebinarId(value);
    // Auto-fill landing URL from the picked webinar's signup link (editable).
    const w = webinars.find((x) => x.webinar_id === value);
    if (w?.signup_url) setLandingUrl(w.signup_url);
  };

  // Author name picked/typed — sync the photo to the registry: a known author fills
  // their remembered photo, anyone else clears it (so author A's photo can't leak onto
  // a later author B / free-text persona via stale state). The unchanged-value guard
  // ignores MUI's onChange→onInputChange(reset) double-fire so a fresh upload survives.
  const handleAuthorNameChange = (value: string) => {
    if (value === authorName) return;
    setAuthorName(value);
    const match = authors.find((a) => a.name === value);
    setAuthorImageUrl(match?.image_url ?? '');
  };

  // Upload an author photo → BunnyCDN, store the returned CDN URL. The backend
  // remembers name→image on save so the next redlink for this author auto-fills.
  const handleAuthorImageUpload = async (file: File) => {
    setUploadingImage(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('image', file);
      // axios auto-sets the multipart boundary for FormData — fetchData can't
      // (it JSON.stringifies the body + forces application/json).
      const response = await api.post('/api/newsletter-redlinks/upload-author-image', form);
      const body = response.data as { success: boolean; data?: { url: string }; error?: string };
      if (body.success && body.data?.url) {
        setAuthorImageUrl(body.data.url);
        showSnackbar('Autorenbild hochgeladen', 'success');
      } else {
        setError(body.error || 'Bild-Upload fehlgeschlagen');
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error || e.message || 'Bild-Upload fehlgeschlagen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') =>
    setSnackbar({ open: true, message, severity });

  // Create (POST) or update (PUT). Returns true on success.
  const handleSave = useCallback(async (): Promise<boolean> => {
    setError(null);

    if (!headline.trim()) {
      setError('Bitte eine Headline eingeben');
      return false;
    }
    if (!concept.trim()) {
      setError('Bitte ein Concept eingeben');
      return false;
    }
    // Bei 'livestream' setzt der Server die URL; das Feld ist gesperrt und
    // vorbelegt, eine Pflichtprüfung hier würde nur eine Eingabe verlangen, die
    // gar nicht vorgesehen ist.
    if (targetType !== 'livestream' && !landingUrl.trim()) {
      setError('Bitte eine Landing-URL eingeben');
      return false;
    }
    if (targetType === 'plx' && !productId) {
      setError('Bitte ein Produkt auswählen');
      return false;
    }
    if (targetType === 'webinar' && !webinarId) {
      setError('Bitte ein Webinar auswählen');
      return false;
    }

    const currentHtml = getCurrentHtml();
    if (!currentHtml.trim()) {
      setError('Bitte einen Inhalt (Body) eingeben');
      return false;
    }
    // The tracked link lives in the body via a placeholder (like the PLX/webinar
    // mailing editors). Require it so the redlink has a clickable, tracked CTA.
    if (!REDLINK_LINK_TOKEN_RE.test(currentHtml)) {
      setError(
        `Bitte den Link-Platzhalter ${redlinkLinkToken(targetType)} im Text einfügen (wird beim Versand durch den getrackten Link ersetzt).`,
      );
      return false;
    }
    // Das Platzhalter-Menue des Editors bietet „Datum dynamisch" unabhaengig vom Zieltyp an
    // (es steckt in TinyMCEs `init`, das nur beim Mounten gelesen wird — ausblenden waere
    // unzuverlaessig, ein Zieltyp-Wechsel im offenen Dialog griffe nicht). Deshalb hier die
    // Regel, statt das Menue zu verstecken: der Autor erfaehrt, WARUM es nicht geht.
    // Spiegelt `dateTokenIssue` in node-app/src/routes/newsletter-redlinks.ts — der Server
    // bleibt die Autoritaet, das hier erspart nur die 400.
    if (REDLINK_DATE_TOKEN_RE.test(currentHtml) || REDLINK_DATE_TOKEN_RE.test(headline)) {
      if (targetType !== 'webinar') {
        setError(
          'Der Platzhalter {WEBINAR_DATE_DYNAMIC} braucht ein Webinar als Ziel — dieser Redlink ' +
            `ist auf "${targetType}" gestellt. Platzhalter entfernen oder Zieltyp ändern.`,
        );
        return false;
      }
      if (!webinarId) {
        setError('Der Platzhalter {WEBINAR_DATE_DYNAMIC} braucht ein ausgewähltes Webinar.');
        return false;
      }
    }
    setBodyHtml(currentHtml);

    setSaving(true);
    try {
      const payload = {
        target_type: targetType,
        // 'livestream' darf ein Produkt mitschicken (optional, nur Beschriftung).
        product_id: targetType === 'webinar' ? null : productId || null,
        webinar_id: targetType === 'webinar' ? webinarId : null,
        headline,
        body_html: currentHtml,
        landing_url: landingUrl,
        concept,
        wkz_code: targetType === 'plx' && wkzCode ? wkzCode : null,
        author_name: authorName.trim() || null,
        author_image_url: authorName.trim() ? authorImageUrl.trim() || null : null,
      };

      if (redlinkId) {
        const res = await fetchData(`/api/newsletter-redlinks/${redlinkId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (!res.success) {
          setError(res.error || 'Fehler beim Speichern');
          return false;
        }
        showSnackbar('Redlink gespeichert', 'success');
        return true;
      }

      const res = await fetchData<{ id: number }>('/api/newsletter-redlinks', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.success || !res.data) {
        setError(res.error || 'Fehler beim Erstellen');
        return false;
      }
      showSnackbar('Redlink erstellt', 'success');
      // Navigate to the freshly created redlink so the publish controls unlock.
      setSearchParams({ tab: 'editor', id: String(res.data.id) });
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    headline,
    concept,
    landingUrl,
    targetType,
    productId,
    webinarId,
    wkzCode,
    authorName,
    authorImageUrl,
    redlinkId,
    getCurrentHtml,
    setSearchParams,
  ]);

  // Insert the tracked-link placeholder as an anchor at the cursor (WYSIWYG) — the
  // copywriter edits the visible text. {PRODUCT_LINK}/{WEBINAR_LINK} is resolved to
  // the banner/affiliate URL at send (like the PLX/webinar mailing editors).
  const linkToken = redlinkLinkToken(targetType);

  /**
   * Deckt sich der geplante Pin-Tag mit einer geplanten Live-Sendung?
   *
   * Der Redlink wird beim Versand nur ausgespielt, wenn an seinem Pin-Tag eine
   * BESTÄTIGTE Sendung läuft — sonst übernimmt der Evergreen-PLX-Redlink den
   * Platz. Das ist gewolltes Verhalten, aber ohne Hinweis hier bemerkt es
   * niemand: die Zeile bleibt "aktiv" und taucht trotzdem nie auf.
   *
   * Bewusst nur ein Hinweis, keine Sperre: der Sendeplan kann sich nach dem
   * Anlegen noch ändern, und die Bestätigung fällt ohnehin erst am Sendetag.
   */
  const livestreamHint: { severity: 'info' | 'success' | 'warning'; text: string } = (() => {
    const day = pinDate || scheduledDate;
    if (!day) {
      return livestreams.length
        ? {
            severity: 'info',
            text: `${livestreams.length} Sendung(en) geplant — nächste am ${moment(livestreams[0].date).format('DD.MM.YYYY')} um ${livestreams[0].time} Uhr.`,
          }
        : { severity: 'warning', text: 'Aktuell ist keine Live-Sendung geplant.' };
    }
    const match = livestreams.find((l) => l.date === day);
    if (!match) {
      return {
        severity: 'warning',
        text: `Am ${moment(day).format('DD.MM.YYYY')} ist keine Live-Sendung geplant — die Anzeige würde an diesem Tag nicht ausgespielt.`,
      };
    }
    return {
      severity: match.status === 'confirmed' ? 'success' : 'info',
      text:
        `Sendung am ${moment(match.date).format('DD.MM.YYYY')} um ${match.time} Uhr` +
        (match.status === 'confirmed'
          ? ' (bestätigt).'
          : ' (noch nicht bestätigt — die Bestätigung erfolgt am Sendetag).'),
    };
  })();
  const insertLinkPlaceholder = () => {
    const snippet = `<a href="${linkToken}">Jetzt mehr erfahren</a>`;
    if (editorMode === 'wysiwyg' && editorRef.current) {
      editorRef.current.insertContent(snippet);
    } else {
      // Code mode: append to the source; the copywriter can reposition it.
      const next = `${getCurrentHtml()}\n<p>${snippet}</p>`;
      setBodyHtml(next);
      if (monacoRef.current) monacoRef.current.setValue(next);
    }
  };

  // Pin to a date + activate. 409 ⇒ another active redlink already on that date.
  const handleSchedule = async () => {
    if (!redlinkId) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(pinDate)) {
      showSnackbar('Bitte ein gültiges Datum wählen', 'error');
      return;
    }
    setScheduling(true);
    setError(null);
    try {
      const res = await fetchData<{ id: number; scheduled_date: string; status: RedlinkStatus }>(
        `/api/newsletter-redlinks/${redlinkId}/schedule`,
        { method: 'POST', body: JSON.stringify({ scheduled_date: pinDate }) },
      );
      if (res.success) {
        setStatus('active');
        setScheduledDate(pinDate);
        showSnackbar(`Redlink für ${moment(pinDate).format('DD.MM.YYYY')} eingeplant`, 'success');
      } else {
        // Backend returns HTTP 409 with a German message for a date collision;
        // fetchData surfaces it as { success:false, error }.
        showSnackbar(res.error || 'An diesem Tag ist bereits ein Redlink eingeplant', 'error');
      }
    } catch (err) {
      showSnackbar((err as Error).message, 'error');
    } finally {
      setScheduling(false);
    }
  };

  // Activate as evergreen (auto-rotation) — clears any pinned date.
  const handleActivateEvergreen = async () => {
    if (!redlinkId) return;
    setActivating(true);
    setError(null);
    try {
      const res = await fetchData(`/api/newsletter-redlinks/${redlinkId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'active', scheduled_date: null }),
      });
      if (res.success) {
        setStatus('active');
        setScheduledDate(null);
        setPinDate('');
        showSnackbar('Redlink als Evergreen aktiviert (Auto-Rotation)', 'success');
      } else {
        showSnackbar(res.error || 'Fehler beim Aktivieren', 'error');
      }
    } catch (err) {
      showSnackbar((err as Error).message, 'error');
    } finally {
      setActivating(false);
    }
  };

  // Test send — saves latest content first, then renders today's newsletter with
  // this redlink and sends it via SMTP. Surfaces the backend warning if present.
  const handleSendTest = async (
    emails: string[],
    _clientCodes: string[],
  ): Promise<{ success: boolean; message?: string }> => {
    if (!redlinkId) return { success: false, message: 'Bitte Redlink erst speichern' };

    const saved = await handleSave();
    if (!saved) return { success: false, message: error || 'Speichern fehlgeschlagen' };

    try {
      const res = await fetchData<{
        sent_to: string;
        selection_tier: string;
        warning: string | null;
      }>(`/api/newsletter-redlinks/${redlinkId}/send-test`, {
        method: 'POST',
        body: JSON.stringify({ email: emails[0] }),
      });
      if (!res.success) {
        return { success: false, message: res.error || 'Fehler beim Senden' };
      }
      const warning = res.data?.warning;
      if (warning) {
        showSnackbar(warning, 'warning');
      } else {
        showSnackbar(`Test-Newsletter an ${res.data?.sent_to} gesendet`, 'success');
      }
      return { success: true };
    } catch (err) {
      return { success: false, message: (err as Error).message };
    }
  };

  // Read-only attribution token preview (so the copywriter sees the banner code).
  const bannerPreview = `FT_${concept || '?'}_RDL_${moment().format('YYYYMMDD')}_RDL_ID-${
    redlinkId ?? '?'
  }_`;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Redlink-Details</Typography>
          <Chip
            size="small"
            label={STATUS_LABELS[status]}
            color={STATUS_COLORS[status]}
            variant={status === 'draft' ? 'outlined' : 'filled'}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ziel
            </Typography>
            <ToggleButtonGroup
              value={targetType}
              exclusive
              onChange={handleTargetTypeChange}
              size="small"
            >
              <ToggleButton value="plx">PLX-Produkt</ToggleButton>
              <ToggleButton value="webinar">Webinar</ToggleButton>
              <ToggleButton value="livestream">Live-Sendung</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {targetType === 'plx' ? (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Produkt</InputLabel>
                  <Select
                    value={productId}
                    label="Produkt"
                    onChange={(e) => {
                      setProductId(e.target.value as number);
                      setWkzCode('');
                    }}
                  >
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>WKZ (optional)</InputLabel>
                  <Select
                    value={wkzCode}
                    label="WKZ (optional)"
                    onChange={(e) => setWkzCode(e.target.value)}
                    disabled={!productId}
                  >
                    <MenuItem value="">Kein WKZ</MenuItem>
                    {wkzConfigs.map((w) => (
                      <MenuItem key={w.wkz_code} value={w.wkz_code}>
                        {w.wkz_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : targetType === 'webinar' ? (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Webinar</InputLabel>
                <Select
                  value={webinarId}
                  label="Webinar"
                  onChange={(e) => handleWebinarChange(e.target.value)}
                >
                  {webinars.map((w) => (
                    <MenuItem key={w.webinar_id} value={w.webinar_id}>
                      {w.title} ({formatGermanTime(w.webinar_date, 'DD.MM.YYYY')})
                      {/* Bewerbungsstatus nur ANZEIGEN, nicht sperren — der Server gatet beim
                          Ausspielen; hier soll man sehen, warum ein Redlink leer bleiben wird. */}
                      {w.promo_label && (
                        <Chip
                          size="small"
                          label={w.promo_label}
                          color={w.promo_status === 'bewerbbar' ? 'success' : 'warning'}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <>
              {/* Live-Sendung: kein Pflicht-Ziel. Die Sendung wird über das
                  eingeplante Datum bestimmt, nicht über eine Auswahl hier — es
                  gibt keine Per-Sendung-Adresse. Das Produkt ist optional und
                  dient nur der Beschriftung in der Auswertung; bewertet werden
                  Bestellungen über den tatsächlich bestellten Artikel. */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Produkt in der Sendung (optional)</InputLabel>
                  <Select
                    value={productId}
                    label="Produkt in der Sendung (optional)"
                    onChange={(e) => setProductId(e.target.value as number)}
                  >
                    <MenuItem value="">Kein Produkt</MenuItem>
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Nur Beschriftung — die Umsatzzuordnung läuft über den bestellten Artikel.
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Alert severity={livestreamHint.severity} sx={{ py: 0.5 }}>
                  {livestreamHint.text}
                </Alert>
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Headline"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Concept"
              value={concept}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/\s/g, '');
                if (value.length <= 10) setConcept(value);
              }}
              inputProps={{ maxLength: 10 }}
              required
              helperText="1-10 Zeichen, keine Leerzeichen (Banner-Attribution, z.B. RDL1)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Link(s) platzierst du selbst im Text: Platzhalter{' '}
                <Box component="code" sx={{ fontFamily: 'monospace', bgcolor: '#f0f0f0', px: 0.5 }}>
                  {linkToken}
                </Box>{' '}
                als <code>href</code> nutzen (Text &amp; Position frei wählbar) — wird beim Versand
                durch den getrackten Link ersetzt.
              </Typography>
              <Button size="small" variant="outlined" onClick={insertLinkPlaceholder}>
                Link einfügen
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Bei 'livestream' setzt der Server die URL — Feld gesperrt und
                ohne Erreichbarkeitsprüfung. Ein editierbares Feld hier wäre eine
                Lüge: der Wert würde beim Speichern verworfen. */}
            <TextField
              fullWidth
              label="Landing-URL"
              value={targetType === 'livestream' ? LIVESTREAM_LANDING_URL : landingUrl}
              onChange={(e) => setLandingUrl(e.target.value)}
              onBlur={() =>
                targetType === 'livestream' ? undefined : validateUrl('landing_url', landingUrl)
              }
              required={targetType !== 'livestream'}
              placeholder="https://..."
              error={
                targetType !== 'livestream' &&
                landingUrlState.reachable === false &&
                !landingUrlState.cross_domain_redirect
              }
              helperText={
                targetType === 'livestream'
                  ? 'Fest vorgegeben — der Server setzt diese URL, der Link wird mit ?rdl= getrackt.'
                  : landingUrlState.validating
                    ? 'URL wird geprüft...'
                    : landingUrlState.reachable === true
                      ? 'URL erreichbar'
                      : landingUrlState.cross_domain_redirect
                        ? `Weiterleitung zu anderer Domain: ${landingUrlState.redirect_url}`
                        : landingUrlState.error
                          ? landingUrlState.error
                          : ' '
              }
              InputProps={{
                readOnly: targetType === 'livestream',
                endAdornment: (
                  <InputAdornment position="end">
                    {landingUrlState.validating && <CircularProgress size={18} />}
                    {landingUrlState.reachable === true &&
                      !landingUrlState.cross_domain_redirect && (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                    {landingUrlState.cross_domain_redirect && (
                      <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                    )}
                    {landingUrlState.reachable === false &&
                      !landingUrlState.cross_domain_redirect && (
                        <ErrorIcon color="error" fontSize="small" />
                      )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Banner-Attribution (Vorschau)"
              value={bannerPreview}
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
              helperText="Read-only — so wird der Redlink im Newsletter-Banner getrackt."
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Autor (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wird als „Gastbeitrag von {'{Name}'}" mit rechtsbündigem Foto angezeigt. Ohne Autor
          erscheint keine Byline.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={authors.map((a) => a.name)}
              value={authorName}
              onChange={(_, value) => handleAuthorNameChange(value ?? '')}
              onInputChange={(_, value) => handleAuthorNameChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Autor-Name"
                  placeholder="z.B. Carsten Müller"
                  helperText="FT-Autor wählen oder freien Namen tippen"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                src={authorImageUrl || undefined}
                alt={authorName || 'Autor'}
                sx={{ width: 56, height: 56 }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleAuthorImageUpload(file);
                }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={!authorName.trim() || uploadingImage}
                startIcon={uploadingImage ? <CircularProgress size={16} /> : undefined}
                onClick={() => fileInputRef.current?.click()}
              >
                {authorImageUrl ? 'Bild ersetzen' : 'Bild hochladen'}
              </Button>
              {authorImageUrl && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  disabled={uploadingImage}
                  onClick={() => setAuthorImageUrl('')}
                >
                  Entfernen
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Inhalt</Typography>
          <ToggleButtonGroup
            value={editorMode}
            exclusive
            onChange={handleEditorModeChange}
            size="small"
          >
            <ToggleButton value="wysiwyg">
              <EditIcon sx={{ mr: 0.5 }} /> WYSIWYG
            </ToggleButton>
            <ToggleButton value="code">
              <CodeIcon sx={{ mr: 0.5 }} /> Code
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
          {editorMode === 'wysiwyg' ? (
            <TinyMCEEditor
              ref={editorRef}
              value={bodyHtml}
              onChange={(content) => setBodyHtml(content)}
            />
          ) : (
            <Editor
              height="500px"
              defaultLanguage="html"
              value={bodyHtml}
              onChange={(value) => setBodyHtml(value || '')}
              onMount={(editorInstance) => {
                monacoRef.current = editorInstance;
              }}
              options={{ minimap: { enabled: false }, wordWrap: 'on' }}
            />
          )}
        </Box>

        <SpamScorePanel
          editorType="redlink"
          referenceId={redlinkId ?? null}
          subject={headline}
          preheader=""
          body={bodyHtml}
          fromName="Finanztrends"
        />
      </Paper>

      {/* Publish controls — available once the redlink has been saved. */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Veröffentlichen
        </Typography>
        {!redlinkId ? (
          <Alert severity="info">
            Bitte den Redlink zuerst speichern, um ihn zu veröffentlichen.
          </Alert>
        ) : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Datum"
                type="date"
                value={pinDate}
                onChange={(e) => setPinDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={scheduling ? <CircularProgress size={18} /> : <ScheduleIcon />}
                onClick={handleSchedule}
                disabled={scheduling || !pinDate}
              >
                Für Datum einplanen
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              {/* Evergreen ist ausschliesslich ein PLX-Modus: die Rotation
                  waehlt per SQL `target_type = 'plx'`. Fuer webinar UND
                  livestream waere der Knopf ein Versprechen, das die Auswahl
                  nicht einloest — die Zeile wuerde 'aktiv' ohne Datum
                  dastehen und nie ausgespielt. */}
              <Tooltip
                title={
                  targetType === 'webinar'
                    ? 'Webinar-Redlinks können nur für ein Datum eingeplant werden'
                    : targetType === 'livestream'
                      ? 'Live-Sendungs-Redlinks können nur für ein Datum eingeplant werden — sie bewerben einen konkreten Termin'
                      : 'Nimmt am täglichen Auto-Rotation-Pool teil'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={activating ? <CircularProgress size={18} /> : <AutorenewIcon />}
                    onClick={handleActivateEvergreen}
                    disabled={activating || targetType !== 'plx'}
                  >
                    Als Evergreen aktivieren
                  </Button>
                </span>
              </Tooltip>
            </Grid>
            {scheduledDate && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Aktuell eingeplant für {moment(scheduledDate).format('DD.MM.YYYY')}.
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {/* Action buttons */}
      <Box display="flex" justifyContent="space-between" gap={2}>
        <Button variant="outlined" onClick={onCancel}>
          Abbrechen
        </Button>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={() => setTestEmailOpen(true)}
            disabled={!redlinkId}
          >
            Test senden
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving || hasUnreachableUrls() || isValidating()}
          >
            {saving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </Box>
      </Box>

      <TestEmailDialog
        open={testEmailOpen}
        onClose={() => setTestEmailOpen(false)}
        onSend={handleSendTest}
        availableClients={[REDLINK_TEST_CLIENT]}
        defaultSelectedClients={[REDLINK_TEST_CLIENT.code]}
        subtitle="Rendert den heutigen Newsletter mit diesem Redlink und sendet ihn an die angegebene Adresse."
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RedlinkEditor;
